import {jsx} from 'panel';

import {ContextPanelEl} from 'common/widgets/context-panel';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {promptSharing} from 'common/widgets/sharing';
import {getProjectId, getReportUrl} from 'common/report/util';
import {showMetricsApiErrorToast, updateMetric} from 'common/report/queries/app-api/metrics';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {hasNewPanelFormat} from 'common/widgets/context-panel/util';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {ModelStatus} from 'common/util/model-resource/model-resource';
import {getEditCapability, getShareCapability} from 'common/report/util/saved-entities';
import {MetricsResultsMap} from 'app_api/projects/metrics/types';

import type {ContextPanelRendererParams} from '../child-menu';

/**
 * Provides handlers for events inside a metric context panel
 */
export const createContextPanelEventHandlers = (params: Partial<ContextPanelRendererParams> = {}) => {
  // handles actions from, the context panel
  const handleContextPanelAction = async (ev: CustomEvent) => {
    const action = ev.detail.name;
    const metricId = ev.detail.item.id;

    if (action === `shareFormula`) {
      await promptSharing({
        type: SharableEntityType.Metric,
        id: metricId,
        'project-id': Number(getProjectId()),
      });
    } else if (ev.detail.item.url && action === `viewEntity`) {
      window.open(ev.detail.item.url, `_blank`);
    } else {
      throw new Error(`action handler not implemented`);
    }
  };

  const handleContextPanelEditDescription = async ({detail}: CustomEvent) => {
    const metricId = detail.item.id;
    const updatedDescription = detail.description;

    let results: MetricsResultsMap;
    try {
      results = await updateMetric({
        metricId,
        metric: {
          description: updatedDescription,
        },
      });
    } catch {
      results = null;
      showMetricsApiErrorToast();
    }

    if (results) {
      params.availableContexts.metrics.update(results, {partialUpdate: false});
    }
  };

  return {
    hoverIn: params.handleHoverIn,
    hoverOut: params.handleHoverOut,
    tooltipContextAction: handleContextPanelAction,
    tooltipContextEditDescription: handleContextPanelEditDescription,
  };
};

/**
 * Renders and handles context panel actions for a Metric
 * @see Metric
 */
export const renderMetricContextPanel = (params: ContextPanelRendererParams) => {
  if (!params.tooltipData) {
    return null;
  }

  return (
    <ContextPanelEl
      attrs={{
        ...params.contextPanelAttrs,
        'tooltip-data': params.tooltipData,
        elRef: `contextPanel`,
      }}
      props={params.contextPanelProps}
      on={createContextPanelEventHandlers(params)}
    />
  );
};

/**
 * Generates the context panel data for Metrics to allow for sharing and description editing
 */
export function generateFormulaContextPanelData({
  id,
  metricsContext,
  canWriteMetrics = false,
  canShare = false,
}: {
  id: number;
  metricsContext: MetricsResource;
  canWriteMetrics?: boolean;
  canShare?: boolean;
}): ContextPanelData {
  const model = metricsContext.getOne(id);
  const metric = model.value;

  if (model.status === ModelStatus.Loading || !metric) {
    return null;
  }

  const permissions = getEditCapability(metric);
  const actions = [];
  const links = [];
  const details = [];

  // manage sharing and permissions action
  if (canShare) {
    actions.push({
      name: `shareFormula`,
      label: `Share`,
      icon: `invite`,
      // Only superadmin or editor can share
      disabled: !getShareCapability(metric, EntityType.Metric).enableShare,
      disabledTooltipText: `You have view-only access and cannot share`,
      item: {
        id: metric.id,
        superadminOverrideShare: getShareCapability(metric).requireSuperadminOverride,
      },
    });
  }

  // view entity in lexicon link
  links.push({
    icon: `share`,
    href: getReportUrl(`lexicon`, {projectId: getProjectId()}) + `#transformations/formulas/${metric.id}`,
    tooltipText: `View in Lexicon`,
  });

  if (hasNewPanelFormat()) {
    actions.push({
      name: `viewEntity`,
      label: `View in Lexicon`,
      icon: `share`,
      item: {
        id: metric.id,
        name: metric.name,
        url: getReportUrl(`lexicon`, {projectId: getProjectId()}) + `#transformations/formulas/${metric.id}`,
      },
    });

    if (metric.created_by) {
      details.push({name: `Creator`, type: `user`, value: {...metric.created_by}});
    }
  }

  return {
    actions,
    links,
    header: metric.name,
    subHeader: `Formula`,
    entityIcon: `formula-scroll`,
    description: metric.description,
    entityType: EntityType.Metric,
    createdBy: metric.created_by,
    excludedMetricIcons: [`pie`, `pulse`],
    details,
    // allows for editing the description of the metric
    editItem:
      canWriteMetrics && permissions.enableEdit
        ? {
            id: metric.id,
            type: EntityType.Metric,
            name: metric.name,
          }
        : null,
  };
}
